import { JSX, createMemo, createSignal, splitProps } from 'solid-js';
import { DOMElement } from 'solid-js/jsx-runtime';

import Loading from './Loading';
import { Toggle } from './Toggle';
import { cx } from '../utils';

interface ClickHandler<T> {
  (e: MouseEvent & { currentTarget: HTMLButtonElement; target: DOMElement }): T;
}
export interface ButtonProps<T = any> extends Omit<JSX.ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'onClick'> {
  type?: ButtonType;
  onClick?: ClickHandler<T>;
  size?: 's' | 'm' | 'l' | 'xl' | 'xs';
  timeout?: number; // 超时时间，单位 ms
  loading?: boolean; // 允许外部受控
}

const types = {
  brand: 'button-brand',
  second: 'button-second',
  icon: 'button-icon',
  iconFilled: 'button-icon-filled',
};

const sizes = {
  s: 'button-s',
  m: 'button-m',
  l: 'button-l',
  xl: 'button-xl',
  xs: 'button-xs',
};
type ButtonType = keyof typeof types;

/**
 * 按钮，添加了几个常用的样式，
 * 然后添加了loading逻辑
 * 1、onClick事件返回Promise就会自动触发loading
 * // onClick事件触发transition也会触发loading(solid的transition貌似理解有误，不能这样使用2)
 * @param props
 * @returns
 */
export default function Button(props: ButtonProps) {
  const [local, others] = splitProps(props, ['type', 'onClick', 'size', 'timeout', 'loading', 'disabled']);
  const [innerLoading, setInnerLoading] = createSignal(false);

  // 外部和内部 loading
  const isLoading = createMemo(() => local.loading ?? innerLoading());

  const onClick = (e: any) => {
    if (local.disabled || isLoading()) return;

    const result = local.onClick?.(e);

    // 判断是否是异步操作
    if (result instanceof Promise || (result && typeof result.then === 'function')) {
      setInnerLoading(true);

      // 超时竞争
      const timeoutLimt = local.timeout || 15000; // 默认 15 秒超时

      const timer = setTimeout(() => {
        if (innerLoading()) {
          setInnerLoading(false);
        }
      }, timeoutLimt);

      // 清理
      Promise.resolve(result).finally(() => {
        clearTimeout(timer);
        setInnerLoading(false);
      });
    }
  };

  return (
    <button
      {...others}
      disabled={local.disabled || isLoading()}
      class={cx('button', sizes[local.size || 'm'], types[local.type!], props.class)}
      onClick={onClick}
    >
      <Toggle when={!isLoading()} fallback={<Loading class="text-alw_white" />}>
        {props.children}
      </Toggle>
    </button>
  );
}
